import { useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useRestartLogin } from './useRestartLogin';
import { useSessionExpiry } from './useSessionExpiry';
import SessionExpiredModal from './SessionExpiredModal';

function hasAuthParams() {
  const p = new URLSearchParams(window.location.search);
  // AAD y OIDC devuelven al menos code + state
  return p.has('code') || p.has('state') || p.has('id_token') || p.has('session_state');
}

const detectIMCallback = () => {
  const url = window.location;
  const searchParams = new URLSearchParams(url.search);
  const hashParams = new URLSearchParams(url.hash.substring(1));

  // Parámetros de callback en query string
  const queryCallbackParams = ['code', 'state', 'error', 'error_description'];
  const hasQueryCallback = queryCallbackParams.some(param => searchParams.has(param));

  // Parámetros de callback en fragment
  const fragmentCallbackParams = ['access_token', 'id_token', 'code', 'error'];
  const hasFragmentCallback = fragmentCallbackParams.some(param => hashParams.has(param));

  return hasQueryCallback || hasFragmentCallback;
};

/**
 * Este componente maneja la autenticación automática.
 * react-oidc-context maneja automáticamente los callbacks del IM.
 */
export function AutoLogin({ children }) {
  const auth = useAuth();
  const [callbackProcessed, setCallbackProcessed] = useState(false);
  const [urlCleaned, setUrlCleaned] = useState(false);
  const [redirectInitiated, setRedirectInitiated] = useState(false);
  const [refreshAttempted, setRefreshAttempted] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [recoveryAttempted, setRecoveryAttempted] = useState(false);
  const [isRecovering, setIsRecovering] = useState(false);

  // Vive en useRestartLogin porque lo comparte con el aviso de sesión vencida (#168):
  // que los dos llamen a lo mismo es lo que evita que se separen con el tiempo.
  const restartLogin = useRestartLogin();
  // AutoLogin es el único dueño de decidir qué ve el usuario cuando la sesión está rota (#168).
  const motivoSesionVencida = useSessionExpiry();

  useEffect(() => {
    // Debug: mostrar información sobre el estado de autenticación
    console.log('AutoLogin - Auth state:', {
      isLoading: auth.isLoading,
      isAuthenticated: auth.isAuthenticated,
      activeNavigator: auth.activeNavigator,
      hasAuthParams: hasAuthParams(),
      isCallback: detectIMCallback(),
      callbackProcessed
    });

    // Si el provider está navegando (haciendo callback/renew), NO dispares redirect
    if (auth.activeNavigator) {
      console.log('AutoLogin - Provider is navigating, waiting...');
      return;
    }

    // Si estamos cargando, esperar
    if (auth.isLoading) {
      console.log('AutoLogin - Still loading, waiting...');
      return;
    }

    // Detectar si hay parámetros de callback en la URL
    const hasCallbackParams = hasAuthParams() || detectIMCallback();

    // Si venimos del IM con parámetros de callback, procesar una sola vez
    if (hasCallbackParams && !callbackProcessed) {
      console.log('AutoLogin - Detected callback, processing...');
      setCallbackProcessed(true);
      return;
    }

    // Si ya procesamos el callback y estamos autenticados, esperar un tiempo y redirigir a la raíz
    if (callbackProcessed && auth.isAuthenticated && hasCallbackParams && !redirectInitiated) {
      console.log('AutoLogin - Callback processed and authenticated, waiting before redirect to root');
      setRedirectInitiated(true);

      // Esperar 1.5 segundos antes de redirigir
      setTimeout(() => {
        console.log('AutoLogin - Redirecting to root');
        window.location.replace('/');
      }, 1500);

      return;
    }

    // Si ya procesamos el callback y estamos autenticados (sin params o URL ya limpia), continuar normalmente
    if (callbackProcessed && auth.isAuthenticated && (!hasCallbackParams || urlCleaned)) {
      console.log('AutoLogin - Callback processed and authenticated, showing app');
      return;
    }

    // Si no está autenticado Y no hay parámetros de callback, redirigí al login
    if (!auth.isAuthenticated && !hasCallbackParams && !auth.error && !isRefreshing) {
      console.log('AutoLogin - Not authenticated, redirecting to login');
      auth.signinRedirect();
    }

    // Sesión guardada inservible: hay error, no venimos de un callback y no estamos
    // autenticados. Antes esto era un callejón sin salida — el guard `!auth.error` de
    // arriba impedía el redirect y la pantalla decía "Redirigiendo al login…" para
    // siempre. Se limpia la sesión y se reintenta UNA vez; si vuelve a fallar, cae al
    // UI de error con el motivo real.
    //
    // "Failed to fetch" queda afuera a propósito: es el servidor de autenticación
    // inalcanzable, no una sesión podrida. Redirigir ahí sólo cambia nuestro mensaje
    // por un error del navegador.
    if (auth.error &&
      !auth.isAuthenticated &&
      !hasCallbackParams &&
      !recoveryAttempted &&
      !isRecovering &&
      auth.error.message !== 'Failed to fetch' &&
      auth.error.message !== "Session doesn't have required client") {
      console.log('AutoLogin - Sesión inválida, limpiando y reintentando login');
      setRecoveryAttempted(true);
      setIsRecovering(true);
      restartLogin()
        .catch((err) => {
          console.error('AutoLogin - El reintento de login falló:', err);
          setIsRecovering(false);
        });
      return;
    }

    // Manejar error específico de sesión expirada intentando silent refresh
    if (auth.error &&
      auth.error.message === "Session doesn't have required client" &&
      !refreshAttempted &&
      !isRefreshing) {
      console.log('AutoLogin - Detected session error, attempting silent refresh...');
      setRefreshAttempted(true);
      setIsRefreshing(true);

      auth.signinSilent()
        .then(() => {
          console.log('AutoLogin - Silent refresh successful');
          setIsRefreshing(false);
        })
        .catch((err) => {
          console.error('AutoLogin - Silent refresh failed:', err);
          setIsRefreshing(false);
          // El error original de auth.error persistirá, mostrando el UI de error después
        });
    }
  }, [auth, callbackProcessed, urlCleaned, redirectInitiated, refreshAttempted, isRefreshing,
      recoveryAttempted, isRecovering, restartLogin]);

  // Mostrar loading mientras se procesa la autenticación.
  //
  // Sólo cuando NO hay usuario todavía: esta pantalla es para el login inicial. Una renovación
  // en segundo plano sobre una sesión existente pasa por signinSilent, que también setea
  // activeNavigator — y sin esta condición la app se reemplazaría por un cartel de carga cada
  // vez que se renueva el token, incluso cuando la renovación es exitosa e invisible (#168).
  if ((auth.isLoading || auth.activeNavigator) && !auth.user) {
    return <div>Procesando autenticación…</div>;
  }

  // Si hay parámetros de callback PERO aún no está autenticado Y no hemos limpiado la URL, mostrar loading
  if ((hasAuthParams() || detectIMCallback()) && !auth.isAuthenticated && !urlCleaned) {
    return <div>Procesando callback de autenticación…</div>;
  }

  // Si estamos intentando un refresh silencioso o limpiando una sesión inservible
  if (isRefreshing || isRecovering) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: '20px' }}>
        <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#00468B' }}>
          Verificando sesión…
        </div>
        <div style={{ color: '#666' }}>
          Intentando reconectar automáticamente.
        </div>
      </div>
    );
  }

  // La sesión estaba viva y dejó de servir: se avisa SIN desmontar la app, para que el usuario
  // no pierda lo que estaba haciendo. Va antes del bloque de error a propósito: un fallo de
  // renovación llega también como auth.error, y si cayera ahí el usuario vería la pantalla
  // completa de abajo — que es para quien nunca llegó a entrar y no tiene nada atrás que
  // preservar. Tener dos presentaciones para el mismo hecho es el modo de falla de #131.
  if (motivoSesionVencida && auth.user) {
    return (
      <>
        {children}
        <SessionExpiredModal open />
      </>
    );
  }

  // Un fallo de renovación llega TAMBIEN como auth.error (source 'renewSilent'). Sobre una sesión
  // existente no puede caer en la pantalla completa de abajo: mientras los reintentos corren, el
  // usuario tiene que seguir viendo su pantalla, y si terminan mal el aviso de arriba se encarga.
  // Sin esta condición había hasta cinco minutos de app desmontada con el token todavía válido y
  // la API respondiendo 200 — exactamente lo contrario de lo que este cambio viene a hacer.
  const esFalloDeRenovacion = auth.error?.source === 'renewSilent';

  // Si hay error de autenticación, mostrarlo y dar opción de reconectar
  if (auth.error && !(esFalloDeRenovacion && auth.user)) {
    console.error('AutoLogin - Auth error:', auth.error);

    // Si es el error que ya intentamos refrescar y falló, o cualquier otro error
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: '20px' }}>
        <div style={{ color: '#ef4444', fontSize: '1.2rem', fontWeight: 'bold' }}>
          La sesión ha expirado o hubo un problema de conexión
        </div>
        <div style={{ color: '#666' }}>
          {auth.error.message === 'Failed to fetch'
            ? 'No se pudo contactar al servidor de autenticación. Verifique su conexión.'
            : `Error: ${auth.error.message}`}
        </div>
        <button
          onClick={() => { restartLogin(); }}
          style={{
            padding: '10px 20px',
            backgroundColor: '#0056b3',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '1rem'
          }}
        >
          Volver a ingresar
        </button>
      </div>
    );
  }

  // Si no está autenticado, el efecto de arriba ya disparó el redirect al login.
  // Va DESPUÉS del bloque de error a propósito: cuando estaba antes, tapaba ese UI y
  // dejaba al usuario mirando "Redirigiendo al login…" sin que hubiera redirect alguno
  // (ver #136). El mensaje sólo debe aparecer cuando efectivamente estamos yendo al login.
  if (!auth.isAuthenticated) {
    return <div>Redirigiendo al login…</div>;
  }

  // Si estamos esperando el redirect después de autenticarnos
  if (redirectInitiated && callbackProcessed && auth.isAuthenticated) {
    return <div>Autenticación exitosa. Redirigiendo…</div>;
  }

  // Si ya está autenticado, muestra la aplicación
  return children;
}
